import { ActionArgs, stopChild } from 'xstate';
import {
  Buyer,
  FilteredApplication,
  MerchantPaymentProduct,
} from '@ads-bread/shared/bread/codecs';
import { validateOfferProductTypes } from '@ads-bread/shared/bread/util';
import { ActorErrorParams } from '../../../apply/lib/types';
import {
  AnalyticsEvent,
  setApplication as setApplicationAnalytics,
  setSelectedOffer as setSelectedOfferAnalytics,
  track,
} from '../../lib/analytics';
import { getMaxCapacities } from '../../lib/capacity';
import { logger } from '../../lib/logger';
import { groupOffersByType } from '../../lib/offers';
import {
  ApplicationCheckoutResult,
  ApplicationPrepareCheckoutResult,
  CreateApplicationResult,
  FetchApplicationResult,
  FetchLatestApplicationResult,
  ReCreateApplicationResult,
} from './types/actors';
import { ApplicationContext } from './applicationMachine';
import {
  ApplicationMachineEvents,
  SendAssignSelectedOfferParams,
} from './types/events';
import { filterApplicationCapacity } from './utils';

type ApplicationActionArgs = ActionArgs<
  ApplicationContext,
  ApplicationMachineEvents,
  ApplicationMachineEvents
>;

/**
 * Stops XState machine actors related to the application process.
 * @return void
 */
export const stopStateMachineActorRefs = (): void => {
  stopChild('estimatedSpendMachine');
  stopChild('fraudAlloyMachine');
};

/**
 * Handles the completion of the application process by resolving
 * the provided promise with the application result.
 * @param _ (context, event, self)
 * @param params
 * @returns void
 */
export const handleApplicationDone = (
  _: ApplicationActionArgs,
  params:
    | FetchApplicationResult
    | ReCreateApplicationResult
    | ApplicationCheckoutResult
    | ApplicationPrepareCheckoutResult
): void => {
  params.resolve(params.applicationRes);
};

/**
 * Rejects the pending application promise with the actor error.
 * @param _ (context, event, self)
 * @param params
 * @returns void
 */
export const handleApplicationServiceError = (
  _: ApplicationActionArgs,
  params: ActorErrorParams
): void => {
  params.reject(params.error);
};

/**
 * Reports the current application to the analytics layer when present
 * on the actor result.
 * @param _ (context, event, self)
 * @param params
 * @returns void
 */
export const handleApplicationAnalytics = (
  _: ApplicationActionArgs,
  params:
    | FetchLatestApplicationResult
    | CreateApplicationResult
    | ApplicationCheckoutResult
    | ApplicationPrepareCheckoutResult
): void => {
  const app = params.applicationRes.result;
  if (app) {
    setApplicationAnalytics(app);
  }
};

/**
 * Tracks the `CheckoutCompleted` analytics event after a successful
 * application checkout.
 * @returns void
 */
export const handleApplicationCheckoutCompleteAnalytics = (): void => {
  track(AnalyticsEvent.CheckoutCompleted);
};

/**
 * Tracks the `OrderSubmitted` analytics event when the buyer submits the
 * order from the review page (i.e. when the checkout request is dispatched),
 * independent of the eventual approval / checkout outcome.
 * @returns void
 */
export const handleApplicationOrderSubmittedAnalytics = (): void => {
  track(AnalyticsEvent.OrderSubmitted);
};

/**
 * Tracks the `AutoPayEnrollmentSuccess` analytics event when the
 * application checkout enrolled an auto-payment method.
 * @param _ (context, event, self)
 * @param params
 * @returns void
 */
export const handleAutoPayEnrollmentSuccess = (
  _: ApplicationActionArgs,
  params: ApplicationCheckoutResult
): void => {
  const app = params.applicationRes.result;
  if (app?.autoPaymentMethodID) {
    track(AnalyticsEvent.AutoPayEnrollmentSuccess);
  }
};

/**
 * Reports the selected offer to the analytics layer.
 * @param _ (context, event, self)
 * @param params
 * @returns void
 */
export const handleSelectedOfferAnalytics = (
  _: ApplicationActionArgs,
  params: SendAssignSelectedOfferParams
): void => {
  setSelectedOfferAnalytics(params.selectedOffer);
};

/**
 * Updates the estimated-spend child machine using the fetched application
 * totals, preserving any previously-emitted estimated spend values.
 * @param args ApplicationActionArgs
 * @param params
 * @returns void
 */
export const emitFetchedApplicationEstimatedSpend = (
  { context: ctx }: ApplicationActionArgs,
  params: FetchLatestApplicationResult | FetchApplicationResult
): void => {
  const { estimatedSpendMachineActorRef } = ctx.stateMachineActorRefs;

  if (!estimatedSpendMachineActorRef) {
    throw new Error('Estimated spend actor ref is null!');
  }

  const app = params.applicationRes.result;

  if (!app) {
    throw new Error('Application is null!');
  }

  const {
    context: { estimatedSpend },
  } = estimatedSpendMachineActorRef.getSnapshot();

  estimatedSpendMachineActorRef.send({
    type: 'SEND_UPDATE_ESTIMATED_SPEND',
    params: {
      estimatedSpend: {
        INSTALLMENTS: estimatedSpend.INSTALLMENTS
          ? estimatedSpend.INSTALLMENTS
          : app.order.totalPrice.value / 100,
        SPLITPAY: estimatedSpend.SPLITPAY
          ? estimatedSpend.SPLITPAY
          : app.order.totalPrice.value / 100,
        HYBRID_LINE_OF_CREDIT: estimatedSpend.HYBRID_LINE_OF_CREDIT
          ? estimatedSpend.HYBRID_LINE_OF_CREDIT
          : app.order.totalPrice.value / 100,
      },
    },
  });
};

/**
 * Replays events that arrived while child machines were still
 * initializing by sending each one back through self.
 * @param args ApplicationActionArgs
 * @returns void
 */
export const processDeferredInitializationEvents = ({
  context: ctx,
  self,
}: ApplicationActionArgs): void => {
  ctx.deferredInitializationEvents.forEach((event) => {
    self.send(event);
  });
};

type EmitCreatedApplicationEstimatedSpendDeps = {
  merchantPaymentProducts: MerchantPaymentProduct[];
};

/**
 * Factory for `emitCreatedApplicationEstimatedSpend`. Requires the merchant's
 * configured payment products to compute approved capacities.
 * @param deps
 * @returns action that emits the estimated spend for a newly-created application
 */
export const createEmitCreatedApplicationEstimatedSpend =
  (deps: EmitCreatedApplicationEstimatedSpendDeps) =>
  (
    { context: ctx }: ApplicationActionArgs,
    params: CreateApplicationResult
  ): void => {
    const { estimatedSpendMachineActorRef } = ctx.stateMachineActorRefs;

    if (!estimatedSpendMachineActorRef) {
      throw new Error('Estimated spend actor ref is null!');
    }

    const app = params.applicationRes.result;

    if (!app) {
      throw new Error('Application is null!');
    }

    const offers = validateOfferProductTypes(
      app.offers || [],
      deps.merchantPaymentProducts
    );

    const approvedPaymentProductIDs = offers.map(
      (offer) => offer.paymentProduct.id
    );

    const approvedPaymentProducts = deps.merchantPaymentProducts?.filter((pp) =>
      approvedPaymentProductIDs.includes(pp.paymentProduct.id)
    );

    const paymentAgreementsByType = groupOffersByType(offers);

    const {
      INSTALLMENTS: installmentsMaxCapacity,
      SPLITPAY: splitPayMaxCapacity,
      HYBRID_LINE_OF_CREDIT: hybridLineOfCreditMaxCapacity,
    } = getMaxCapacities(paymentAgreementsByType, approvedPaymentProducts);

    estimatedSpendMachineActorRef.send({
      type: 'SEND_UPDATE_ESTIMATED_SPEND',
      params: {
        estimatedSpend: {
          INSTALLMENTS: installmentsMaxCapacity.value / 100,
          SPLITPAY: splitPayMaxCapacity.value / 100,
          HYBRID_LINE_OF_CREDIT: hybridLineOfCreditMaxCapacity.value / 100,
        },
      },
    });
  };

type HandleOnApprovedDeps = {
  onApproved?: (data: FilteredApplication) => void;
  canViewCapacity: boolean;
};

/**
 * Factory for `handleOnApproved`. Wraps the integrator-supplied `onApproved`
 * callback and the capacity-visibility flag.
 * @param deps
 * @returns action that invokes the integrator `onApproved` callback
 */
export const createHandleOnApproved =
  (deps: HandleOnApprovedDeps) =>
  (_: ApplicationActionArgs, params: CreateApplicationResult): void => {
    const application = params.applicationRes.result;

    if (application) {
      deps.onApproved?.(
        filterApplicationCapacity(application, deps.canViewCapacity)
      );
    }
  };

type HandleOnCheckoutDeps = {
  onCheckout?: (data: FilteredApplication) => void;
  contextBuyer: Buyer | null;
  canViewCapacity: boolean;
};

/**
 * Factory for `handleOnCheckout`. Wraps the integrator-supplied `onCheckout`
 * callback, the current buyer (for logging), and the capacity-visibility flag.
 * @param deps
 * @returns action that invokes the integrator `onCheckout` callback
 */
export const createHandleOnCheckout =
  (deps: HandleOnCheckoutDeps) =>
  (_: ApplicationActionArgs, params: ApplicationContext): void => {
    const application = params.application;

    /**
     * Report if no application was passed in event. Remove after confirming
     * in production. This should not be possible
     */
    if (!application) {
      logger.error(
        { buyerID: deps.contextBuyer?.id },
        'handleOnCheckout called without application'
      );
      return;
    }

    /**
     * Report if integrator onCheckout event is missing. This should not be
     * possible unless this is a carts experience
     */
    if (!deps.onCheckout) {
      logger.error(
        { applicationID: application.id, buyerID: deps.contextBuyer?.id },
        'handleOnCheckout called without onCheckout callback'
      );
      return;
    }

    if (application) {
      const checkoutData = filterApplicationCapacity(
        application,
        deps.canViewCapacity
      );
      logger.info(
        {
          applicationID: checkoutData.id,
          buyerID: checkoutData.buyerID,
        },
        'handleOnCheckout called'
      );

      deps.onCheckout?.(checkoutData);
    }
  };

type HasReviewedApplicationDeps = {
  contextBuyerID: string | null;
  isExchangedStreamlined: boolean;
  setHasReviewedApplication: (value: boolean) => void;
};

/**
 * Factory for `hasReviewedApplication`. Skips the bookkeeping update for
 * exchanged buyers unless the streamlined exchange flow is enabled.
 * @param deps
 * @returns action that marks the application as reviewed in local state
 */
export const createHasReviewedApplication =
  (deps: HasReviewedApplicationDeps) =>
  (_: ApplicationActionArgs, __: FetchLatestApplicationResult): void => {
    const isExchangedBuyer = !!deps.contextBuyerID;
    if (isExchangedBuyer && !deps.isExchangedStreamlined) {
      return;
    }
    deps.setHasReviewedApplication(true);
  };

type LogErrorDeps = {
  merchantID: string | null;
};

/**
 * Factory for `logError`. Captures the merchant ID for structured log
 * enrichment.
 * @param deps
 * @returns action that logs an error with application and merchant context
 */
export const createLogError =
  (deps: LogErrorDeps) =>
  ({ context: ctx }: ApplicationActionArgs, params: ActorErrorParams): void => {
    logger.error(
      {
        err: params.error,
        merchantID: deps.merchantID,
        applicationID: ctx.application?.id,
        buyerID: ctx.application?.buyerID,
      },
      `Error in ApplicationMachineContext: ${params.type}`
    );
  };
